import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { env } from '~/helpers/env'

/**
 * Téléchargement de l'attestation d'une session.
 *
 * Extrait de `MySessions` parce que l'écran de parcours l'offre aussi : la
 * danse fetch → blob → lien éphémère est le genre de code qui se recopie mal,
 * et un `revokeObjectURL` oublié dans la copie fuit sans que personne ne le voie.
 *
 * Le PDF passe par `fetch` et non par un `<a href>` : la route exige le cookie
 * de session, et le nom de fichier est décidé par l'API dans son
 * `Content-Disposition` — c'est elle qui connaît le nom de la formation.
 *
 * Sans `forUserId`, c'est l'attestation du porteur de la session ; avec, celle d'un
 * apprenant, téléchargée par l'encadrement depuis sa fiche (#242).
 */
export function useCertificateDownload(forUserId?: string) {
  const { t } = useTranslation()
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const download = async (sessionId: string) => {
    setDownloadingId(sessionId)
    try {
      const path = forUserId ? `/certificates/session/${sessionId}/user/${forUserId}` : `/certificates/session/${sessionId}`
      const res = await fetch(`${env.api.url}${path}`, {
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`certificate download failed (${res.status})`)
      const blob = await res.blob()
      const fileName = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1]
        ?? 'attestation.pdf'
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error(t('home.certificate.error'))
    } finally {
      setDownloadingId(null)
    }
  }

  return { download, downloadingId }
}
